// ─── I pezzi comuni dell'area maestro ────────────────────────────────────────
//
// ⚖️ `etichettaTipo` esiste già (⇒ `features/calendario/Griglia.tsx`), e non si
// importa da lì apposta: quel file è la griglia della segreteria — 1000 righe
// pensate per un monitor largo — e tirarselo dentro vorrebbe dire far scaricare
// a un telefono, a bordo campo, la schermata da cui il maestro è stato tenuto
// fuori.
// 📌 *Riusare una riga di un modulo vuol dire caricare il modulo.*

import type { QueryClient } from '@tanstack/react-query';

export const ETICHETTA_TIPO: Readonly<Record<string, string>> = {
  partita: 'Partita',
  lezione: 'Lezione',
  allenamento: 'Allenamento',
  manutenzione: 'Manutenzione',
  torneo: 'Torneo',
  corso: 'Corso',
};

/**
 * 🔁⭐ **DOVE SI RINFRESCA LA GIORNATA, in un posto solo.**
 *
 * Ogni gesto del maestro — mettere un allievo, prenotare, spostare, annullare,
 * iscrivere — cambia **tre** schermate: la **giornata** di un giorno,
 * l'**agenda** che sfoglia quattordici giorni, e l'elenco degli **spazi**
 * liberi. Erano una sola fino al 20/09, e ogni mutazione invalidava quella
 * chiave a mano.
 * 🚨 Il difetto che questa funzione esiste per non far ricapitare: chi aggiunge
 * la quarta schermata (i soldi del mese) dovrebbe ricordarsi di **sette** posti
 * diversi, e quello che dimentica non dà nessun errore — mostra un numero
 * vecchio, che è la cosa peggiore che una schermata possa fare.
 * 📌 *Una cosa da ricordare in sette punti è una cosa che prima o poi si
 *    ricorda in sei.*
 *
 * 💶🔄 **E LA QUARTA SCHERMATA È ARRIVATA (tappa ④b, 20/09): la riga qui sopra
 *    era al futuro e adesso è al passato.** I soldi del mese sono la **sesta**
 * chiave, e non sono facoltativi: la cifra del mese sta **anche in fondo
 * all'agenda**, quindi senza questa riga un gesto qualunque — mettere un
 * allievo, annullare una volta — lascerebbe lì la cifra di prima. Ed è
 * esattamente il difetto che il paragrafo qui sopra descriveva: nessun errore,
 * un numero vecchio.
 *
 * 🚨🔄⭐⭐ **E `spazi` È ENTRATO QUI IL 20/09, DOPO AVERLO MISURATO ADDOSSO:
 *    prima stava scritto **solo** dentro l'annullo.** 📏 Il difetto, visto con
 * un browser vero sul banco: preso un posto alle 09:00, si torna a prenotare e
 * la schermata offre **ancora le 09:00**. La risposta vecchia resta buona per
 * trenta secondi (`staleTime: 30_000`, ⇒ `main.tsx`) e in quella finestra
 * l'elenco mostra libera un'ora che non lo è più — il server la rifiuta, e chi
 * guarda ha toccato una riga che la schermata gli aveva appena offerto.
 * ⚖️ La riga vecchia dell'annullo si **corregge**, non si affianca: due posti
 * che invalidano la stessa chiave sono la garanzia che uno dei due, un giorno,
 * resti indietro — ed era già successo, perché a prenotare non ci aveva pensato
 * nessuno.
 * 📌 *Un elenco di cose libere invecchia al contrario di tutti gli altri: non
 *    manca una riga, ne offre una che non c'è più.*
 *
 * 🏖️🔄⭐⭐ **E LE ASSENZE SONO LA SETTIMA (tappa ⑥, 20/09), per una ragione che
 *    va al contrario delle altre sei.** Un gesto del maestro non cambia i
 * giorni in cui non c'è — quelli li scrive lui, da un'altra parte — ma cambia
 * il **numero di volte che cadono dentro** un'assenza, che è l'unica cosa che
 * dice ciò che l'assenza **non** ha fatto (⇒ `NonCiSono.tsx`). ⇒ Annullata una
 * lezione di quei giorni, senza questa riga la schermata continuerebbe a dire
 * «3 volte già in campo» quando ne restano due — cioè userebbe un numero vecchio
 * proprio nella riga che serve a fidarsi.
 * ⚖️ Il verso opposto — scrivere un'assenza — **non** passa di qui: lo fa la
 *    sezione che la scrive, e invalida gli **spazi**, che sono la sola cosa che
 *    cambia.
 *
 * 🎓🔄⭐⭐ **E I CORSI SONO ENTRATI IL 20/09 (tappa ③), per lo stesso difetto in
 *    un altro posto.** Annullando una volta **dalla scheda della lezione**, il
 * corso a monte non se ne accorgeva: l'elenco continuava a dire «ancora 5
 * volte» e la scheda del corso mostrava fra le sue volte una data che non
 * esisteva più. ⇒ Le chiavi sono **sette**, e la ragione per cui stanno qui
 * dentro invece che sparse è esattamente questa: la quinta l'avrebbe
 * dimenticata chi tocca i corsi senza pensare alla giornata, e viceversa.
 */
export function rinfrescaLaGiornata(coda: QueryClient): void {
  for (const chiave of [
    ['maestro', 'giornata'],
    ['maestro', 'agenda'],
    ['maestro', 'spazi'],
    ['maestro', 'corsi'],
    ['maestro', 'corso'],
    ['maestro', 'soldi'],
    ['maestro', 'assenze'],
  ]) {
    void coda.invalidateQueries({ queryKey: chiave });
  }
}

/**
 * `lun 21`, per una tacca larga 44 px. La usano le due strisce di giorni
 * dell'area maestro — l'agenda e la scelta dell'ora — che così si leggono uguali.
 */
export function tacca(data: string): { giorno: string; numero: string } {
  const d = new Date(`${data}T00:00:00Z`);
  const GIORNI = ['dom', 'lun', 'mar', 'mer', 'gio', 'ven', 'sab'];
  return { giorno: GIORNI[d.getUTCDay()] ?? '', numero: String(d.getUTCDate()) };
}
